/**
 * Modal-style form for editing a provider's configuration fields.
 *
 * Each `ProviderKind` declares the fields the operator needs to fill
 * in (API key, service-account JSON, kubeconfig, …). The form is
 * generic over those declarations, so adding a new SkyPilot-supported
 * cloud only requires adding metadata in `api/local/providers.ts`.
 *
 * Secret fields are AES-GCM encrypted server-side; the WebUI only sees
 * `key_last4` (for the catalog's primary field) and a list of which
 * secret fields have *some* value stored at this scope. The form
 * therefore renders "stored / not stored" pills rather than the
 * legacy last-4 readout for non-primary secrets.
 */
import { useMemo, useState } from "react";
import { AlertTriangle, Eye, EyeOff, ExternalLink, Radio } from "lucide-react";

import {
  providerDocsUrl,
  type ProviderEntry,
  type ProviderField,
  type ProviderMeta,
} from "@/api/local/providers";
import { Pill } from "@/components/Pill";

import { Modal } from "./Modal";

export interface ProviderConfigPatch {
  values: Record<string, string>;
  note?: string;
  rotated_at?: string;
  /**
   * When true, ask the controller to bust the SkyServe yaml-hash cache
   * for every Pool that uses this provider kind — the next reconcile
   * tick will then drive a `sky.serve.update(..., mode='ROLLING')` so
   * the freshly written credentials are pushed onto running serve
   * controller VMs. Forwarded as `?propagate=true` on the underlying
   * PUT. Defaults to undefined / false; the form's "propagate to
   * running services" checkbox is the only place that flips it.
   */
  propagate?: boolean;
}

interface ProviderConfigFormProps {
  open: boolean;
  onClose: () => void;
  meta: ProviderMeta;
  /**
   * Scope-aware label rendered as the modal subtitle (e.g.
   * "Global catalog" / "service: chat-llm" / "pool #4").
   */
  scopeLabel: string;
  entry: ProviderEntry | undefined;
  /**
   * Persists the operator's edits. The modal awaits the returned Promise
   * and only closes on success — surfacing failures inline (instead of
   * the previous "fire-and-forget then close, silently rolling back the
   * optimistic patch") so the operator can see *why* a save bounced.
   */
  onSave: (patch: ProviderConfigPatch) => Promise<void>;
  /**
   * Clear / forget the stored secret values. Called by the "clear all
   * credentials" footer button. The optional `propagate` flag mirrors
   * the form's checkbox so callers can route it as `?propagate=true`
   * on the controller's `POST /providers/{id}/rotate` (panic-kill
   * pattern: drop the row's secret on disk *and* fail every running
   * provisioning attempt for this kind until a new key is PUT).
   */
  onClear?: (opts?: { propagate?: boolean }) => Promise<void> | void;
}

export function ProviderConfigForm({
  open,
  onClose,
  meta,
  scopeLabel,
  entry,
  onSave,
  onClear,
}: ProviderConfigFormProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={`Configure ${meta.label}`}
      subtitle={
        <span className="muted" style={{ fontSize: 12 }}>
          {scopeLabel} · {meta.hint}
        </span>
      }
      headerExtras={
        <a
          href={providerDocsUrl(meta)}
          target="_blank"
          rel="noreferrer noopener"
          className="btn btn--ghost btn--sm"
        >
          <ExternalLink />
          SkyPilot docs
        </a>
      }
    >
      <ProviderConfigBody
        meta={meta}
        entry={entry}
        onSave={onSave}
        onSaved={onClose}
        onClear={onClear}
        onCleared={onClose}
        onCancel={onClose}
      />
    </Modal>
  );
}

interface BodyProps {
  meta: ProviderMeta;
  entry: ProviderEntry | undefined;
  onSave: (patch: ProviderConfigPatch) => Promise<void>;
  /** Called after `onSave` resolves successfully (closes the modal). */
  onSaved: () => void;
  onClear?: (opts?: { propagate?: boolean }) => Promise<void> | void;
  /** Called after `onClear` resolves successfully. */
  onCleared: () => void;
  onCancel: () => void;
}

/**
 * Provider categories whose `kind` actually appears in
 * `placements.source_cloud` on the controller side (= the kinds that
 * are forwarded into SkyPilot's `resources.cloud:` field). The
 * propagate-to-running-services checkbox is gated to these because
 * for `kubernetes` / `storage` / `registry` kinds the controller's
 * propagator filters them out (see `_NON_CLOUD_KINDS` in
 * `controller/skypilot/placements.py`) and the flag would be a
 * confusing no-op.
 */
const PROPAGATABLE_CATEGORIES = new Set(["compute"]);

function ProviderConfigBody({
  meta,
  entry,
  onSave,
  onSaved,
  onClear,
  onCleared,
  onCancel,
}: BodyProps) {
  const initialDrafts = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of meta.fields) {
      // Secrets are never sent to the browser, so leave them blank for
      // editing (the placeholder + "currently stored" pill tell the
      // operator that something is on file). Non-secret fields are
      // pre-filled from the cached row so the form is editable rather
      // than re-entry-only.
      const stored = entry?.values?.[f.key];
      out[f.key] = !f.secret && stored ? stored : "";
    }
    return out;
  }, [meta, entry]);

  const [drafts, setDrafts] = useState<Record<string, string>>(initialDrafts);
  const [note, setNote] = useState(entry?.note ?? "");
  const [secretsDirty, setSecretsDirty] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState<"save" | "clear" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // "Propagate to running services" — opt-in. Default off so a routine
  // catalog edit (e.g. typing in a fresh key, flipping `enabled`)
  // never accidentally rolls every Service that uses this kind. The
  // checkbox is rendered only for categories whose kind actually
  // reaches SkyPilot's `resources.cloud:` (compute clouds today —
  // kubernetes / storage / registry kinds get filtered out by the
  // controller's propagator and the toggle would be a confusing
  // no-op there).
  const propagatable = PROPAGATABLE_CATEGORIES.has(meta.category);
  const [propagate, setPropagate] = useState(false);

  const setField = (f: ProviderField, raw: string) => {
    setDrafts((p) => ({ ...p, [f.key]: raw }));
    if (f.secret) setSecretsDirty((p) => ({ ...p, [f.key]: true }));
  };

  const handleSave = async () => {
    if (busy) return;
    const patch: Record<string, string> = {};
    let touchedSecret = false;
    for (const f of meta.fields) {
      const raw = drafts[f.key] ?? "";
      if (f.secret) {
        // Only push secret edits when the user actually typed something —
        // an empty box means "leave the stored secret alone".
        if (secretsDirty[f.key] && raw !== "") {
          patch[f.key] = raw;
          touchedSecret = true;
        }
      } else {
        patch[f.key] = raw;
      }
    }
    setBusy("save");
    setError(null);
    try {
      await onSave({
        values: patch,
        note,
        ...(touchedSecret ? { rotated_at: new Date().toISOString() } : {}),
        // Only forward `propagate` for kinds where it actually does
        // something — otherwise we'd send `?propagate=true` for a
        // huggingface / cloudflare / kubernetes save, the controller
        // would compute a noop, and the events feed would carry a
        // misleading "credential propagation triggered" entry.
        ...(propagatable && propagate ? { propagate: true } : {}),
      });
      onSaved();
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const handleClear = async () => {
    if (!onClear || busy) return;
    setBusy("clear");
    setError(null);
    try {
      await onClear(
        // Same gate as the Save path: propagate only for compute
        // kinds. The "panic-kill" pattern is most useful here too —
        // if the operator suspects the key is compromised, they
        // probably want every running Service to fail provisioning
        // immediately rather than ride out the next reconcile tick
        // on the now-cleared on-disk creds.
        propagatable && propagate ? { propagate: true } : undefined,
      );
      onCleared();
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {error && (
        <div className="flash flash--info" style={{ marginBottom: 12 }}>
          <AlertTriangle
            style={{
              width: 14,
              height: 14,
              verticalAlign: "middle",
              marginRight: 6,
            }}
          />
          Save failed: {error}
        </div>
      )}
      <div className="form-grid">
        {meta.fields.map((f) => (
          <FieldRow
            key={f.key}
            field={f}
            value={drafts[f.key] ?? ""}
            stored={entry?.values?.[f.key]}
            secretStored={
              f.secret ? (entry?.secret_keys?.includes(f.key) ?? false) : false
            }
            secretLast4={
              f.secret && f.key === meta.primaryField
                ? (entry?.key_last4 ?? null)
                : null
            }
            onChange={(v) => setField(f, v)}
          />
        ))}
        <label>note</label>
        <div className="field">
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="optional operator note (e.g. prod account)"
          />
          <div className="field-hint">
            Free-form note shown next to the entry in the catalog.
          </div>
        </div>
      </div>

      {propagatable && (
        <div
          style={{
            marginTop: 16,
            paddingTop: 14,
            borderTop: "1px solid var(--edge)",
          }}
        >
          <label
            className="toolbar"
            style={{
              gap: 10,
              alignItems: "flex-start",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={propagate}
              onChange={(e) => setPropagate(e.target.checked)}
              disabled={busy !== null}
              style={{ marginTop: 2 }}
            />
            <span>
              <strong style={{ display: "block", marginBottom: 2 }}>
                <Radio
                  style={{
                    width: 13,
                    height: 13,
                    verticalAlign: "-2px",
                    marginRight: 6,
                  }}
                />
                Propagate to running services
              </strong>
              <span className="muted" style={{ fontSize: 12 }}>
                After saving, push the new credentials onto every
                running SkyServe controller VM that uses{" "}
                <strong>{meta.label}</strong> by triggering a rolling
                update on each affected Pool. Leave off to only update
                the stored row + this controller pod&rsquo;s on-disk
                state.
              </span>
            </span>
          </label>

          {propagate && (
            <div
              className="flash flash--warn"
              style={{
                marginTop: 12,
                padding: "10px 12px",
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <AlertTriangle
                  style={{
                    width: 16,
                    height: 16,
                    flexShrink: 0,
                    marginTop: 1,
                  }}
                />
                <div>
                  <strong>Heads-up — this triggers a rolling update.</strong>
                  <ul
                    style={{
                      margin: "6px 0 0",
                      paddingLeft: 18,
                      display: "grid",
                      gap: 3,
                    }}
                  >
                    <li>
                      Every Service / Pool whose placements include{" "}
                      <strong>{meta.label}</strong> will roll. No
                      requests are dropped (SkyServe sprays traffic to
                      old + new during the update), but{" "}
                      <strong>provisioning cost spikes</strong> while
                      the new replicas come up alongside the old set.
                    </li>
                    <li>
                      <strong>KV-cache state on existing replicas is
                      lost</strong>{" "}
                      as they drain. First-token latency for ongoing
                      conversations spikes briefly after the rollout.
                    </li>
                    <li>
                      RunPod / spot capacity exhaustion can stall the
                      rollout if the cloud cannot temporarily fit{" "}
                      <strong>both</strong> the old and new replica
                      sets at the same time.
                    </li>
                    <li>
                      Pressing <em>clear all credentials</em> with this
                      box checked is the <strong>panic-kill</strong>{" "}
                      pattern: every affected Pool will start failing
                      provisioning until you PUT a fresh key. Use only
                      when you need the old key revoked instantly.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div
        className="toolbar"
        style={{
          marginTop: 14,
          paddingTop: 14,
          borderTop: "1px solid var(--edge)",
          justifyContent: "flex-end",
        }}
      >
        {onClear && (
          <button
            type="button"
            className="btn btn--danger btn--sm"
            onClick={handleClear}
            disabled={busy !== null}
            style={{ marginRight: "auto" }}
            title={
              propagatable && propagate
                ? "Clear stored creds AND fail every running SkyServe controller VM that uses this kind until a new key is PUT"
                : "Clear stored credentials at this scope"
            }
          >
            {busy === "clear"
              ? "clearing…"
              : propagatable && propagate
                ? "clear & propagate"
                : "clear all credentials"}
          </button>
        )}
        <button
          type="button"
          className="btn btn--ghost"
          onClick={onCancel}
          disabled={busy !== null}
        >
          cancel
        </button>
        <button
          type="button"
          className={
            propagatable && propagate
              ? "btn btn--danger"
              : "btn btn--primary"
          }
          onClick={handleSave}
          disabled={busy !== null}
          title={
            propagatable && propagate
              ? "Save AND roll every running Service / Pool that uses this provider"
              : "Save the credential row (does not touch running services)"
          }
        >
          {busy === "save"
            ? "saving…"
            : propagatable && propagate
              ? "save & propagate"
              : "save"}
        </button>
      </div>
    </>
  );
}

function toErrorMessage(err: unknown): string {
  if (err == null) return "unknown error";
  if (err instanceof Error) {
    // ApiError stores the parsed JSON body on `.body`; FastAPI puts the
    // human-readable detail on `.detail`. Surface that when present so
    // operators see "BACKSTAGE_SECRET_MASTER_KEY is not configured" instead
    // of a bare HTTP code.
    const body = (err as Error & { body?: unknown }).body;
    if (body && typeof body === "object" && "detail" in body) {
      const detail = (body as { detail?: unknown }).detail;
      if (typeof detail === "string" && detail.length > 0) return detail;
    }
    return err.message;
  }
  return String(err);
}

interface FieldRowProps {
  field: ProviderField;
  value: string;
  stored?: string;
  /** For secret fields: whether the server has *some* value at this scope. */
  secretStored: boolean;
  /** Last-4 fingerprint, only set for the catalog's primary secret field. */
  secretLast4: string | null;
  onChange: (next: string) => void;
}

function FieldRow({
  field,
  value,
  stored,
  secretStored,
  secretLast4,
  onChange,
}: FieldRowProps) {
  const [revealed, setRevealed] = useState(false);
  // For non-secret fields the placeholder is the catalog's hint; for
  // secret fields we surface the "leave blank to keep current value"
  // affordance because the input is always blank on open.
  const placeholder = field.secret
    ? secretStored
      ? secretLast4
        ? `leave blank to keep current value (••••${secretLast4})`
        : "leave blank to keep current value"
      : "paste the value"
    : field.placeholder;
  return (
    <>
      <label htmlFor={`pf-${field.key}`}>
        {field.label}
        {field.optional && (
          <span
            className="muted"
            style={{ marginLeft: 6, textTransform: "none", fontSize: 10 }}
          >
            optional
          </span>
        )}
      </label>
      <div className="field">
        {field.type === "textarea" && (
          <textarea
            id={`pf-${field.key}`}
            rows={field.secret ? 4 : 3}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
          />
        )}
        {field.type === "text" && (
          <input
            id={`pf-${field.key}`}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
          />
        )}
        {field.type === "password" && (
          // Wrap the input + reveal button so the eye icon sits inside
          // the field's right edge (the standard pattern operators expect
          // from password managers, GitHub PAT screens, etc.). The button
          // only flips the input's `type` so the value still lives in
          // React state — we never copy it to a separate visible string.
          <div className="field-with-affix">
            <input
              id={`pf-${field.key}`}
              type={revealed ? "text" : "password"}
              autoComplete="off"
              spellCheck={false}
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={placeholder}
            />
            <button
              type="button"
              className="field-affix__btn"
              onClick={() => setRevealed((p) => !p)}
              aria-label={revealed ? "Hide value" : "Show value"}
              aria-pressed={revealed}
              tabIndex={-1}
            >
              {revealed ? <EyeOff /> : <Eye />}
            </button>
          </div>
        )}
        {field.type === "bool" && (
          <label
            className="toolbar"
            style={{ gap: 8, paddingTop: 8, fontSize: 12 }}
          >
            <input
              id={`pf-${field.key}`}
              type="checkbox"
              checked={value === "true" || stored === "true"}
              onChange={(e) => onChange(e.target.checked ? "true" : "")}
            />
            <span className="muted">
              {value === "true" || stored === "true" ? "enabled" : "disabled"}
            </span>
          </label>
        )}
        {field.secret && secretStored && (
          <div className="field-hint">
            currently stored:{" "}
            <Pill tone="ok">{secretLast4 ? `…${secretLast4}` : "✓"}</Pill>
          </div>
        )}
        {field.hint && <div className="field-hint">{field.hint}</div>}
      </div>
    </>
  );
}
